// A turn or step that could not finish: what the person reads under their message or on the failed
// step. Namespace `chat-error`; copy by DESIGN. `input` and `tool` mirror the server's quote refusals
// (src/usage/quote-errors.mjs), which the UI recognises by code or message and shows in the person's language.
// `editingAppUnavailable` is an edit chat that can no longer name its App (refused before sending), and
// `editingAppUnreachable` the server not reading that App just now; both offer `openApps`. `editingPreviewGone` is a
// preview that is no longer available: `continueWithoutPreview` closes it and sends the same message again.
// A send the plan refused (HTTP 402 payment_required) says why in the plan dialog's own words, which open at the
// same time and offer the plan: `planRequired`, `creditsExhausted` and `memberLimitReached` are the dialog's
// sentences (billing `pausedPlanMember`, `pausedCreditsTitle`, `pausedLimitTitle`); `paymentRequired` has no reason.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  input:
    'We couldn’t answer this message. If it has a file attached, try sending it without the file.',
  tool: 'This step didn’t run because its cost couldn’t be confirmed in advance. Try another source.',
  tooLarge:
    'This message has too many attachments. Edit an earlier message or remove a file to continue.',
  unfinished:
    'That request did not finish. Edit an earlier message or remove attachments, then try again.',
  editingAppUnavailable:
    'This chat can no longer change its App. Open the App from Apps and choose Change with Chat to keep working on it.',
  openApps: 'Open Apps',
  editingAppUnreachable:
    'Chat couldn’t open this App’s saved version just now. Try again in a moment, or open the App from Apps.',
  editingPreviewGone:
    'This App’s preview is no longer available. Continue without the preview, or open a saved App from Apps.',
  continueWithoutPreview: 'Continue without the preview',
  planRequired: 'Your company has no active plan.',
  creditsExhausted: 'You’ve used all your credits.',
  memberLimitReached: 'You’ve reached your monthly limit.',
  paymentRequired: 'Your plan doesn’t allow new messages right now.',
};
export type ChatErrorKey = keyof typeof en;
type Copy = Record<ChatErrorKey, string>;

const bg: Copy = {
  input:
    'Не успяхме да отговорим на това съобщение. Ако към него има прикачен файл, опитайте да го изпратите без файла.',
  tool: 'Тази стъпка не беше изпълнена, защото цената ѝ не можа да бъде потвърдена предварително. Опитайте с друг източник.',
  tooLarge:
    'Това съобщение има твърде много прикачени файлове. Редактирайте по-ранно съобщение или премахнете файл, за да продължите.',
  unfinished:
    'Тази заявка не завърши. Редактирайте по-ранно съобщение или премахнете прикачените файлове и опитайте отново.',
  editingAppUnavailable:
    'Този чат вече не може да променя приложението си. Отворете приложението от Приложения и изберете „Промяна в чата“, за да продължите работата по него.',
  openApps: 'Отваряне на Приложения',
  editingAppUnreachable:
    'Чатът не успя да отвори запазената версия на това приложение в момента. Опитайте отново след малко или отворете приложението от Приложения.',
  editingPreviewGone:
    'Прегледът на това приложение вече не е наличен. Продължете без прегледа или отворете запазено приложение от Приложения.',
  continueWithoutPreview: 'Продължете без прегледа',
  planRequired: 'Компанията Ви няма активен план.',
  creditsExhausted: 'Използвахте всичките си кредити.',
  memberLimitReached: 'Достигнахте месечния си лимит.',
  paymentRequired: 'Планът Ви не позволява нови съобщения в момента.',
};
const cs: Copy = {
  input:
    'Na tuto zprávu se nepodařilo odpovědět. Pokud má přiložený soubor, zkuste ji odeslat bez něj.',
  tool: 'Tento krok neproběhl, protože jeho cenu nebylo možné předem potvrdit. Zkuste jiný zdroj.',
  tooLarge:
    'Tato zpráva má příliš mnoho příloh. Chcete-li pokračovat, upravte dřívější zprávu nebo odeberte soubor.',
  unfinished:
    'Požadavek se nedokončil. Upravte dřívější zprávu nebo odeberte přílohy a zkuste to znovu.',
  editingAppUnavailable:
    'Tento chat už nemůže měnit svou aplikaci. Otevřete aplikaci v sekci Aplikace a zvolte Změnit v chatu, abyste na ní mohli dál pracovat.',
  openApps: 'Otevřít Aplikace',
  editingAppUnreachable:
    'Chat teď nemohl otevřít uloženou verzi této aplikace. Zkuste to za chvíli znovu nebo aplikaci otevřete v sekci Aplikace.',
  editingPreviewGone:
    'Náhled této aplikace už není k dispozici. Pokračujte bez náhledu nebo otevřete uloženou aplikaci v sekci Aplikace.',
  continueWithoutPreview: 'Pokračovat bez náhledu',
  planRequired: 'Vaše firma nemá aktivní tarif.',
  creditsExhausted: 'Vyčerpali jste všechny kredity.',
  memberLimitReached: 'Dosáhli jste měsíčního limitu.',
  paymentRequired: 'Váš tarif teď neumožňuje posílat nové zprávy.',
};
const da: Copy = {
  input:
    'Vi kunne ikke svare på denne besked. Hvis der er en fil vedhæftet, så prøv at sende den uden filen.',
  tool: 'Dette trin blev ikke kørt, fordi prisen ikke kunne bekræftes på forhånd. Prøv en anden kilde.',
  tooLarge:
    'Denne besked har for mange vedhæftninger. Rediger en tidligere besked, eller fjern en fil for at fortsætte.',
  unfinished:
    'Anmodningen blev ikke fuldført. Rediger en tidligere besked, eller fjern vedhæftninger, og prøv igen.',
  editingAppUnavailable:
    'Denne chat kan ikke længere ændre sin app. Åbn appen under Apps, og vælg Ændr i chatten for at arbejde videre med den.',
  openApps: 'Åbn Apps',
  editingAppUnreachable:
    'Chatten kunne ikke åbne den gemte version af denne app lige nu. Prøv igen om lidt, eller åbn appen under Apps.',
  editingPreviewGone:
    'Forhåndsvisningen af denne app er ikke længere tilgængelig. Fortsæt uden forhåndsvisningen, eller åbn en gemt app under Apps.',
  continueWithoutPreview: 'Fortsæt uden forhåndsvisningen',
  planRequired: 'Din virksomhed har ingen aktiv plan.',
  creditsExhausted: 'Du har brugt alle dine kreditter.',
  memberLimitReached: 'Du har nået din månedlige grænse.',
  paymentRequired: 'Din plan tillader ikke nye beskeder lige nu.',
};
const de: Copy = {
  input:
    'Wir konnten diese Nachricht nicht beantworten. Falls sie eine Datei enthält, versuchen Sie, sie ohne die Datei zu senden.',
  tool: 'Dieser Schritt wurde nicht ausgeführt, weil die Kosten nicht im Voraus bestätigt werden konnten. Versuchen Sie es mit einer anderen Quelle.',
  tooLarge:
    'Diese Nachricht hat zu viele Anhänge. Bearbeiten Sie eine frühere Nachricht oder entfernen Sie eine Datei, um fortzufahren.',
  unfinished:
    'Diese Anfrage wurde nicht abgeschlossen. Bearbeiten Sie eine frühere Nachricht oder entfernen Sie Anhänge und versuchen Sie es erneut.',
  editingAppUnavailable:
    'Dieser Chat kann die App nicht mehr ändern. Öffnen Sie die App unter „Apps“ und wählen Sie „Mit Chat ändern“, um weiterzuarbeiten.',
  openApps: 'Apps öffnen',
  editingAppUnreachable:
    'Der Chat konnte die gespeicherte Version dieser App gerade nicht öffnen. Versuchen Sie es gleich noch einmal oder öffnen Sie die App unter „Apps“.',
  editingPreviewGone:
    'Die Vorschau dieser App ist nicht mehr verfügbar. Fahren Sie ohne Vorschau fort oder öffnen Sie eine gespeicherte App unter Apps.',
  continueWithoutPreview: 'Ohne Vorschau weitermachen',
  planRequired: 'Ihr Unternehmen hat keinen aktiven Tarif.',
  creditsExhausted: 'Sie haben alle Credits aufgebraucht.',
  memberLimitReached: 'Sie haben Ihr Monatslimit erreicht.',
  paymentRequired: 'Ihr Tarif erlaubt gerade keine neuen Nachrichten.',
};
const el: Copy = {
  input:
    'Δεν μπορέσαμε να απαντήσουμε σε αυτό το μήνυμα. Αν έχει συνημμένο αρχείο, δοκιμάστε να το στείλετε χωρίς το αρχείο.',
  tool: 'Αυτό το βήμα δεν εκτελέστηκε, επειδή το κόστος του δεν μπορούσε να επιβεβαιωθεί εκ των προτέρων. Δοκιμάστε άλλη πηγή.',
  tooLarge:
    'Αυτό το μήνυμα έχει πάρα πολλά συνημμένα. Επεξεργαστείτε ένα προηγούμενο μήνυμα ή αφαιρέστε ένα αρχείο για να συνεχίσετε.',
  unfinished:
    'Το αίτημα δεν ολοκληρώθηκε. Επεξεργαστείτε ένα προηγούμενο μήνυμα ή αφαιρέστε συνημμένα και δοκιμάστε ξανά.',
  editingAppUnavailable:
    'Αυτή η συνομιλία δεν μπορεί πλέον να αλλάξει την εφαρμογή της. Ανοίξτε την εφαρμογή από τις Εφαρμογές και επιλέξτε «Αλλαγή στη συνομιλία» για να συνεχίσετε.',
  openApps: 'Άνοιγμα Εφαρμογών',
  editingAppUnreachable:
    'Η συνομιλία δεν μπόρεσε να ανοίξει την αποθηκευμένη έκδοση αυτής της εφαρμογής αυτή τη στιγμή. Δοκιμάστε ξανά σε λίγο ή ανοίξτε την εφαρμογή από τις Εφαρμογές.',
  editingPreviewGone:
    'Η προεπισκόπηση αυτής της εφαρμογής δεν είναι πλέον διαθέσιμη. Συνεχίστε χωρίς την προεπισκόπηση ή ανοίξτε μια αποθηκευμένη εφαρμογή από τις Εφαρμογές.',
  continueWithoutPreview: 'Συνέχεια χωρίς προεπισκόπηση',
  planRequired: 'Η εταιρεία σας δεν έχει ενεργό πρόγραμμα.',
  creditsExhausted: 'Χρησιμοποιήσατε όλες τις πιστώσεις σας.',
  memberLimitReached: 'Φτάσατε το μηνιαίο όριό σας.',
  paymentRequired: 'Το πρόγραμμά σας δεν επιτρέπει νέα μηνύματα αυτή τη στιγμή.',
};
const es: Copy = {
  input:
    'No pudimos responder a este mensaje. Si tiene un archivo adjunto, prueba a enviarlo sin el archivo.',
  tool: 'Este paso no se ejecutó porque no se pudo confirmar su coste por adelantado. Prueba otra fuente.',
  tooLarge:
    'Este mensaje tiene demasiados archivos adjuntos. Edita un mensaje anterior o quita un archivo para continuar.',
  unfinished:
    'La solicitud no terminó. Edita un mensaje anterior o quita los archivos adjuntos y vuelve a intentarlo.',
  editingAppUnavailable:
    'Este chat ya no puede cambiar su app. Abre la app desde Apps y elige Cambiar en el chat para seguir trabajando en ella.',
  openApps: 'Abrir Apps',
  editingAppUnreachable:
    'El chat no pudo abrir la versión guardada de esta app en este momento. Vuelve a intentarlo en un momento o abre la app desde Apps.',
  editingPreviewGone:
    'La vista previa de esta app ya no está disponible. Continúa sin la vista previa o abre una app guardada desde Apps.',
  continueWithoutPreview: 'Continuar sin la vista previa',
  planRequired: 'Su empresa no tiene ningún plan activo.',
  creditsExhausted: 'Ha usado todos sus créditos.',
  memberLimitReached: 'Ha alcanzado su límite mensual.',
  paymentRequired: 'Su plan no permite enviar mensajes nuevos en este momento.',
};
const et: Copy = {
  input:
    'Me ei saanud sellele sõnumile vastata. Kui sellel on manus, proovi see saata ilma failita.',
  tool: 'Seda sammu ei käivitatud, sest selle kulu ei õnnestunud ette kinnitada. Proovi teist allikat.',
  tooLarge:
    'Sellel sõnumil on liiga palju manuseid. Jätkamiseks muuda varasemat sõnumit või eemalda fail.',
  unfinished: 'Päring ei lõppenud. Muuda varasemat sõnumit või eemalda manused ja proovi uuesti.',
  editingAppUnavailable:
    'See vestlus ei saa enam oma rakendust muuta. Ava rakendus jaotisest Rakendused ja vali Muuda vestluses, et selle kallal edasi töötada.',
  openApps: 'Ava Rakendused',
  editingAppUnreachable:
    'Vestlus ei saanud selle rakenduse salvestatud versiooni praegu avada. Proovi hetke pärast uuesti või ava rakendus jaotisest Rakendused.',
  editingPreviewGone:
    'Selle rakenduse eelvaade pole enam saadaval. Jätka ilma eelvaateta või ava salvestatud rakendus jaotisest Rakendused.',
  continueWithoutPreview: 'Jätka ilma eelvaateta',
  planRequired: 'Sinu ettevõttel pole aktiivset paketti.',
  creditsExhausted: 'Oled kõik krediidid ära kasutanud.',
  memberLimitReached: 'Oled jõudnud oma kuulimiidini.',
  paymentRequired: 'Sinu pakett ei luba praegu uusi sõnumeid saata.',
};
const fi: Copy = {
  input:
    'Emme voineet vastata tähän viestiin. Jos siinä on liitetiedosto, yritä lähettää se ilman tiedostoa.',
  tool: 'Tätä vaihetta ei suoritettu, koska sen kustannusta ei voitu vahvistaa etukäteen. Kokeile toista lähdettä.',
  tooLarge:
    'Tässä viestissä on liian monta liitettä. Jatka muokkaamalla aiempaa viestiä tai poistamalla tiedosto.',
  unfinished:
    'Pyyntö ei valmistunut. Muokkaa aiempaa viestiä tai poista liitteet ja yritä uudelleen.',
  editingAppUnavailable:
    'Tämä chat ei voi enää muuttaa sovellustaan. Avaa sovellus kohdasta Sovellukset ja valitse Muuta chatissa jatkaaksesi sen parissa.',
  openApps: 'Avaa Sovellukset',
  editingAppUnreachable:
    'Chat ei juuri nyt voinut avata tämän sovelluksen tallennettua versiota. Yritä hetken päästä uudelleen tai avaa sovellus kohdasta Sovellukset.',
  editingPreviewGone:
    'Tämän sovelluksen esikatselu ei ole enää saatavilla. Jatka ilman esikatselua tai avaa tallennettu sovellus Sovellukset-osiosta.',
  continueWithoutPreview: 'Jatka ilman esikatselua',
  planRequired: 'Yritykselläsi ei ole voimassa olevaa pakettia.',
  creditsExhausted: 'Olet käyttänyt kaikki krediittisi.',
  memberLimitReached: 'Olet saavuttanut kuukausirajasi.',
  paymentRequired: 'Pakettisi ei salli uusia viestejä juuri nyt.',
};
const fr: Copy = {
  input:
    'Nous n’avons pas pu répondre à ce message. S’il contient un fichier joint, essayez de l’envoyer sans le fichier.',
  tool: 'Cette étape n’a pas été exécutée, car son coût n’a pas pu être confirmé à l’avance. Essayez une autre source.',
  tooLarge:
    'Ce message contient trop de pièces jointes. Modifiez un message précédent ou retirez un fichier pour continuer.',
  unfinished:
    'La demande n’a pas abouti. Modifiez un message précédent ou retirez les pièces jointes, puis réessayez.',
  editingAppUnavailable:
    'Cette conversation ne peut plus modifier son app. Ouvrez l’app depuis Apps et choisissez Modifier dans le chat pour continuer à y travailler.',
  openApps: 'Ouvrir Apps',
  editingAppUnreachable:
    'La conversation n’a pas pu ouvrir la version enregistrée de cette app pour le moment. Réessayez dans un instant ou ouvrez l’app depuis Apps.',
  editingPreviewGone:
    'L’aperçu de cette app n’est plus disponible. Continuez sans l’aperçu ou ouvrez une app enregistrée depuis Apps.',
  continueWithoutPreview: 'Continuer sans l’aperçu',
  planRequired: 'Votre entreprise n’a aucun forfait actif.',
  creditsExhausted: 'Vous avez utilisé tous vos crédits.',
  memberLimitReached: 'Vous avez atteint votre limite mensuelle.',
  paymentRequired: 'Votre forfait ne permet pas d’envoyer de nouveaux messages pour le moment.',
};
const ga: Copy = {
  input:
    'Níorbh fhéidir linn an teachtaireacht seo a fhreagairt. Má tá comhad ceangailte léi, bain triail as í a sheoladh gan an comhad.',
  tool: 'Níor ritheadh an chéim seo mar níorbh fhéidir a costas a dheimhniú roimh ré. Bain triail as foinse eile.',
  tooLarge:
    'Tá an iomarca ceangaltán leis an teachtaireacht seo. Cuir teachtaireacht níos luaithe in eagar nó bain comhad chun leanúint ar aghaidh.',
  unfinished:
    'Níor críochnaíodh an t-iarratas sin. Cuir teachtaireacht níos luaithe in eagar nó bain ceangaltáin, ansin bain triail eile as.',
  editingAppUnavailable:
    'Ní féidir leis an gcomhrá seo a aip a athrú a thuilleadh. Oscail an aip ó Aipeanna agus roghnaigh Athraigh sa chomhrá chun leanúint ar aghaidh léi.',
  openApps: 'Oscail Aipeanna',
  editingAppUnreachable:
    'Níorbh fhéidir leis an gcomhrá leagan sábháilte na haipe seo a oscailt faoi láthair. Bain triail eile as i gceann tamaillín, nó oscail an aip ó Aipeanna.',
  editingPreviewGone:
    'Níl réamhamharc na haipe seo ar fáil a thuilleadh. Lean ar aghaidh gan an réamhamharc, nó oscail aip shábháilte ó Aipeanna.',
  continueWithoutPreview: 'Lean ar aghaidh gan an réamhamharc',
  planRequired: 'Níl plean gníomhach ag do chuideachta.',
  creditsExhausted: 'D’úsáid tú do chreidmheasanna ar fad.',
  memberLimitReached: 'Tá do theorainn mhíosúil bainte amach agat.',
  paymentRequired: 'Ní cheadaíonn do phlean teachtaireachtaí nua faoi láthair.',
};
const hr: Copy = {
  input:
    'Na ovu poruku nismo mogli odgovoriti. Ako ima priloženu datoteku, pokušajte je poslati bez datoteke.',
  tool: 'Ovaj korak nije pokrenut jer njegov trošak nije bilo moguće unaprijed potvrditi. Pokušajte s drugim izvorom.',
  tooLarge:
    'Ova poruka ima previše privitaka. Uredite raniju poruku ili uklonite datoteku da biste nastavili.',
  unfinished:
    'Zahtjev nije dovršen. Uredite raniju poruku ili uklonite privitke pa pokušajte ponovno.',
  editingAppUnavailable:
    'Ovaj chat više ne može mijenjati svoju aplikaciju. Otvorite aplikaciju u odjeljku Aplikacije i odaberite Promijeni u chatu kako biste nastavili raditi na njoj.',
  openApps: 'Otvori Aplikacije',
  editingAppUnreachable:
    'Chat trenutačno nije mogao otvoriti spremljenu verziju ove aplikacije. Pokušajte ponovno za trenutak ili otvorite aplikaciju u odjeljku Aplikacije.',
  editingPreviewGone:
    'Pretpregled ove aplikacije više nije dostupan. Nastavite bez pretpregleda ili otvorite spremljenu aplikaciju iz odjeljka Aplikacije.',
  continueWithoutPreview: 'Nastavi bez pregleda',
  planRequired: 'Vaša tvrtka nema aktivan paket.',
  creditsExhausted: 'Potrošili ste sve kredite.',
  memberLimitReached: 'Dosegnuli ste mjesečno ograničenje.',
  paymentRequired: 'Vaš paket trenutačno ne dopušta nove poruke.',
};
const hu: Copy = {
  input:
    'Erre az üzenetre nem tudtunk válaszolni. Ha fájl van csatolva, próbáld meg fájl nélkül elküldeni.',
  tool: 'Ez a lépés nem futott le, mert a költségét nem sikerült előre megerősíteni. Próbálj másik forrást.',
  tooLarge:
    'Ennek az üzenetnek túl sok melléklete van. A folytatáshoz szerkessz egy korábbi üzenetet, vagy távolíts el egy fájlt.',
  unfinished:
    'A kérés nem fejeződött be. Szerkessz egy korábbi üzenetet, vagy távolítsd el a mellékleteket, majd próbáld újra.',
  editingAppUnavailable:
    'Ez a csevegés már nem tudja módosítani az alkalmazását. Nyisd meg az alkalmazást az Alkalmazások között, és válaszd a Módosítás a csevegésben lehetőséget a folytatáshoz.',
  openApps: 'Alkalmazások megnyitása',
  editingAppUnreachable:
    'A csevegés most nem tudta megnyitni az alkalmazás mentett verzióját. Próbáld újra egy kicsit később, vagy nyisd meg az alkalmazást az Alkalmazások között.',
  editingPreviewGone:
    'Az alkalmazás előnézete már nem érhető el. Folytassa előnézet nélkül, vagy nyisson meg egy mentett alkalmazást az Alkalmazások között.',
  continueWithoutPreview: 'Folytatás előnézet nélkül',
  planRequired: 'A cégnek nincs aktív csomagja.',
  creditsExhausted: 'Elfogyott az összes kreditje.',
  memberLimitReached: 'Elérte a havi keretét.',
  paymentRequired: 'A csomagja jelenleg nem enged új üzeneteket.',
};
const it: Copy = {
  input:
    'Non siamo riusciti a rispondere a questo messaggio. Se contiene un file allegato, prova a inviarlo senza il file.',
  tool: 'Questo passaggio non è stato eseguito perché non è stato possibile confermarne il costo in anticipo. Prova un’altra fonte.',
  tooLarge:
    'Questo messaggio ha troppi allegati. Modifica un messaggio precedente o rimuovi un file per continuare.',
  unfinished:
    'La richiesta non è stata completata. Modifica un messaggio precedente o rimuovi gli allegati, poi riprova.',
  editingAppUnavailable:
    'Questa chat non può più modificare la sua app. Apri l’app da App e scegli Modifica in chat per continuare a lavorarci.',
  openApps: 'Apri App',
  editingAppUnreachable:
    'La chat non è riuscita ad aprire la versione salvata di questa app in questo momento. Riprova tra poco o apri l’app da App.',
  editingPreviewGone:
    'L’anteprima di questa app non è più disponibile. Continua senza l’anteprima oppure apri un’app salvata da App.',
  continueWithoutPreview: 'Continua senza anteprima',
  planRequired: 'La tua azienda non ha un piano attivo.',
  creditsExhausted: 'Hai usato tutti i tuoi crediti.',
  memberLimitReached: 'Hai raggiunto il tuo limite mensile.',
  paymentRequired: 'Il tuo piano al momento non consente nuovi messaggi.',
};
const lt: Copy = {
  input:
    'Nepavyko atsakyti į šią žinutę. Jei prie jos pridėtas failas, pabandykite ją išsiųsti be failo.',
  tool: 'Šis žingsnis nebuvo atliktas, nes nepavyko iš anksto patvirtinti jo kainos. Išbandykite kitą šaltinį.',
  tooLarge:
    'Šioje žinutėje per daug priedų. Norėdami tęsti, redaguokite ankstesnę žinutę arba pašalinkite failą.',
  unfinished:
    'Užklausa nebuvo baigta. Redaguokite ankstesnę žinutę arba pašalinkite priedus ir bandykite dar kartą.',
  editingAppUnavailable:
    'Šis pokalbis nebegali keisti savo programėlės. Atidarykite programėlę skiltyje Programėlės ir pasirinkite Keisti pokalbyje, kad galėtumėte toliau su ja dirbti.',
  openApps: 'Atidaryti Programėles',
  editingAppUnreachable:
    'Pokalbiui šiuo metu nepavyko atidaryti išsaugotos šios programėlės versijos. Netrukus bandykite dar kartą arba atidarykite programėlę skiltyje Programėlės.',
  editingPreviewGone:
    'Šios programėlės peržiūra nebepasiekiama. Tęskite be peržiūros arba atidarykite išsaugotą programėlę skiltyje Programėlės.',
  continueWithoutPreview: 'Tęsti be peržiūros',
  planRequired: 'Jūsų įmonė neturi aktyvaus plano.',
  creditsExhausted: 'Visi kreditai panaudoti.',
  memberLimitReached: 'Pasiektas mėnesio limitas.',
  paymentRequired: 'Jūsų planas šiuo metu neleidžia siųsti naujų žinučių.',
};
const lv: Copy = {
  input:
    'Mēs nevarējām atbildēt uz šo ziņojumu. Ja tam ir pievienots fails, mēģiniet to nosūtīt bez faila.',
  tool: 'Šis solis netika izpildīts, jo tā izmaksas nevarēja apstiprināt iepriekš. Izmēģiniet citu avotu.',
  tooLarge:
    'Šim ziņojumam ir pārāk daudz pielikumu. Lai turpinātu, rediģējiet kādu iepriekšējo ziņojumu vai noņemiet failu.',
  unfinished:
    'Pieprasījums netika pabeigts. Rediģējiet kādu iepriekšējo ziņojumu vai noņemiet pielikumus un mēģiniet vēlreiz.',
  editingAppUnavailable:
    'Šī tērzēšana vairs nevar mainīt savu lietotni. Atveriet lietotni sadaļā Lietotnes un izvēlieties Mainīt tērzēšanā, lai turpinātu darbu ar to.',
  openApps: 'Atvērt Lietotnes',
  editingAppUnreachable:
    'Tērzēšana pašlaik nevarēja atvērt šīs lietotnes saglabāto versiju. Mēģiniet vēlreiz pēc brīža vai atveriet lietotni sadaļā Lietotnes.',
  editingPreviewGone:
    'Šīs lietotnes priekšskatījums vairs nav pieejams. Turpiniet bez priekšskatījuma vai atveriet saglabātu lietotni sadaļā Lietotnes.',
  continueWithoutPreview: 'Turpināt bez priekšskatījuma',
  planRequired: 'Jūsu uzņēmumam nav aktīva plāna.',
  creditsExhausted: 'Visi kredīti ir izmantoti.',
  memberLimitReached: 'Sasniegts mēneša limits.',
  paymentRequired: 'Jūsu plāns pašlaik neļauj sūtīt jaunas ziņas.',
};
const mt: Copy = {
  input:
    'Ma stajniex inwieġbu dan il-messaġġ. Jekk għandu fajl mehmuż, ipprova ibgħatu mingħajr il-fajl.',
  tool: 'Dan il-pass ma tħaddimx għax l-ispiża tiegħu ma setgħetx tiġi kkonfermata minn qabel. Ipprova sors ieħor.',
  tooLarge:
    'Dan il-messaġġ għandu wisq annessi. Editja messaġġ preċedenti jew neħħi fajl biex tkompli.',
  unfinished:
    'Dik it-talba ma tlestietx. Editja messaġġ preċedenti jew neħħi l-annessi, imbagħad erġa’ pprova.',
  editingAppUnavailable:
    'Din iċ-chat ma tistax tibqa’ tibdel l-app tagħha. Iftaħ l-app minn Apps u agħżel Ibdel fiċ-chat biex tkompli taħdem fuqha.',
  openApps: 'Iftaħ Apps',
  editingAppUnreachable:
    'Iċ-chat ma setgħetx tiftaħ il-verżjoni ssejvjata ta’ din l-app bħalissa. Erġa’ pprova fi ftit ħin, jew iftaħ l-app minn Apps.',
  editingPreviewGone:
    'Il-previżjoni ta’ din l-app m’għadhiex disponibbli. Kompli mingħajr il-previżjoni, jew iftaħ app salvata minn Apps.',
  continueWithoutPreview: 'Kompli mingħajr il-preview',
  planRequired: 'Il-kumpanija tiegħek m’għandhiex pjan attiv.',
  creditsExhausted: 'Użajt il-krediti kollha tiegħek.',
  memberLimitReached: 'Wasalt għal-limitu ta’ kull xahar tiegħek.',
  paymentRequired: 'Il-pjan tiegħek ma jippermettix messaġġi ġodda bħalissa.',
};
const nl: Copy = {
  input:
    'We konden dit bericht niet beantwoorden. Als er een bestand is bijgevoegd, probeer het dan zonder het bestand te versturen.',
  tool: 'Deze stap is niet uitgevoerd omdat de kosten niet vooraf konden worden bevestigd. Probeer een andere bron.',
  tooLarge:
    'Dit bericht heeft te veel bijlagen. Bewerk een eerder bericht of verwijder een bestand om verder te gaan.',
  unfinished:
    'Dat verzoek is niet afgerond. Bewerk een eerder bericht of verwijder bijlagen en probeer het opnieuw.',
  editingAppUnavailable:
    'Deze chat kan zijn app niet meer wijzigen. Open de app via Apps en kies Wijzigen in chat om eraan verder te werken.',
  openApps: 'Apps openen',
  editingAppUnreachable:
    'De chat kon de opgeslagen versie van deze app nu niet openen. Probeer het zo opnieuw of open de app via Apps.',
  editingPreviewGone:
    'Het voorbeeld van deze app is niet meer beschikbaar. Ga verder zonder het voorbeeld of open een opgeslagen app vanuit Apps.',
  continueWithoutPreview: 'Verder zonder preview',
  planRequired: 'Je bedrijf heeft geen actief abonnement.',
  creditsExhausted: 'Je hebt al je credits gebruikt.',
  memberLimitReached: 'Je hebt je maandlimiet bereikt.',
  paymentRequired: 'Je abonnement staat nu geen nieuwe berichten toe.',
};
const pl: Copy = {
  input:
    'Nie udało się odpowiedzieć na tę wiadomość. Jeśli ma załączony plik, spróbuj wysłać ją bez pliku.',
  tool: 'Ten krok nie został wykonany, bo nie udało się z góry potwierdzić jego kosztu. Spróbuj innego źródła.',
  tooLarge:
    'Ta wiadomość ma zbyt wiele załączników. Aby kontynuować, edytuj wcześniejszą wiadomość lub usuń plik.',
  unfinished:
    'To żądanie nie zostało ukończone. Edytuj wcześniejszą wiadomość lub usuń załączniki i spróbuj ponownie.',
  editingAppUnavailable:
    'Ten czat nie może już zmieniać swojej aplikacji. Otwórz aplikację w sekcji Aplikacje i wybierz Zmień na czacie, aby dalej nad nią pracować.',
  openApps: 'Otwórz Aplikacje',
  editingAppUnreachable:
    'Czat nie mógł teraz otworzyć zapisanej wersji tej aplikacji. Spróbuj ponownie za chwilę lub otwórz aplikację w sekcji Aplikacje.',
  editingPreviewGone:
    'Podgląd tej aplikacji nie jest już dostępny. Kontynuuj bez podglądu lub otwórz zapisaną aplikację w sekcji Aplikacje.',
  continueWithoutPreview: 'Kontynuuj bez podglądu',
  planRequired: 'Twoja firma nie ma aktywnego planu.',
  creditsExhausted: 'Wszystkie kredyty zostały wykorzystane.',
  memberLimitReached: 'Miesięczny limit został osiągnięty.',
  paymentRequired: 'Twój plan nie pozwala teraz na nowe wiadomości.',
};
const pt: Copy = {
  input:
    'Não conseguimos responder a esta mensagem. Se tiver um ficheiro anexado, experimenta enviá-la sem o ficheiro.',
  tool: 'Este passo não foi executado porque não foi possível confirmar o custo antecipadamente. Experimenta outra fonte.',
  tooLarge:
    'Esta mensagem tem demasiados anexos. Edita uma mensagem anterior ou remove um ficheiro para continuar.',
  unfinished:
    'Esse pedido não terminou. Edita uma mensagem anterior ou remove os anexos e tenta novamente.',
  editingAppUnavailable:
    'Este chat já não pode alterar a sua app. Abre a app a partir de Apps e escolhe Alterar no chat para continuares a trabalhar nela.',
  openApps: 'Abrir Apps',
  editingAppUnreachable:
    'O chat não conseguiu abrir a versão guardada desta app neste momento. Tenta novamente daqui a pouco ou abre a app a partir de Apps.',
  editingPreviewGone:
    'A pré-visualização desta app já não está disponível. Continue sem a pré-visualização ou abra uma app guardada em Apps.',
  continueWithoutPreview: 'Continuar sem a pré-visualização',
  planRequired: 'A sua empresa não tem nenhum plano ativo.',
  creditsExhausted: 'Utilizou todos os seus créditos.',
  memberLimitReached: 'Atingiu o seu limite mensal.',
  paymentRequired: 'O seu plano não permite novas mensagens neste momento.',
};
const ro: Copy = {
  input:
    'Nu am putut răspunde la acest mesaj. Dacă are un fișier atașat, încearcă să-l trimiți fără fișier.',
  tool: 'Acest pas nu a rulat, deoarece costul său nu a putut fi confirmat în avans. Încearcă altă sursă.',
  tooLarge:
    'Acest mesaj are prea multe atașamente. Editează un mesaj anterior sau elimină un fișier pentru a continua.',
  unfinished:
    'Cererea nu s-a finalizat. Editează un mesaj anterior sau elimină atașamentele, apoi încearcă din nou.',
  editingAppUnavailable:
    'Această conversație nu mai poate modifica aplicația sa. Deschide aplicația din Aplicații și alege Modificați în chat ca să continui să lucrezi la ea.',
  openApps: 'Deschide Aplicații',
  editingAppUnreachable:
    'Conversația nu a putut deschide acum versiunea salvată a acestei aplicații. Încearcă din nou peste puțin timp sau deschide aplicația din Aplicații.',
  editingPreviewGone:
    'Previzualizarea acestei aplicații nu mai este disponibilă. Continuați fără previzualizare sau deschideți o aplicație salvată din Aplicații.',
  continueWithoutPreview: 'Continuă fără previzualizare',
  planRequired: 'Compania nu are un plan activ.',
  creditsExhausted: 'Ați folosit toate creditele.',
  memberLimitReached: 'Ați atins limita lunară.',
  paymentRequired: 'Planul dumneavoastră nu permite mesaje noi momentan.',
};
const sk: Copy = {
  input:
    'Na túto správu sa nepodarilo odpovedať. Ak má priložený súbor, skúste ju odoslať bez neho.',
  tool: 'Tento krok neprebehol, pretože jeho cenu nebolo možné vopred potvrdiť. Skúste iný zdroj.',
  tooLarge:
    'Táto správa má príliš veľa príloh. Ak chcete pokračovať, upravte skoršiu správu alebo odstráňte súbor.',
  unfinished:
    'Požiadavka sa nedokončila. Upravte skoršiu správu alebo odstráňte prílohy a skúste to znova.',
  editingAppUnavailable:
    'Tento chat už nemôže meniť svoju aplikáciu. Otvorte aplikáciu v sekcii Aplikácie a zvoľte Zmeniť v chate, aby ste na nej mohli ďalej pracovať.',
  openApps: 'Otvoriť Aplikácie',
  editingAppUnreachable:
    'Chat teraz nemohol otvoriť uloženú verziu tejto aplikácie. Skúste to o chvíľu znova alebo aplikáciu otvorte v sekcii Aplikácie.',
  editingPreviewGone:
    'Náhľad tejto aplikácie už nie je k dispozícii. Pokračujte bez náhľadu alebo otvorte uloženú aplikáciu v sekcii Aplikácie.',
  continueWithoutPreview: 'Pokračovať bez ukážky',
  planRequired: 'Vaša firma nemá aktívny plán.',
  creditsExhausted: 'Minuli ste všetky kredity.',
  memberLimitReached: 'Dosiahli ste mesačný limit.',
  paymentRequired: 'Váš plán teraz neumožňuje posielať nové správy.',
};
const sl: Copy = {
  input:
    'Na to sporočilo nismo mogli odgovoriti. Če ima priloženo datoteko, ga poskusite poslati brez nje.',
  tool: 'Ta korak se ni izvedel, ker njegovih stroškov ni bilo mogoče vnaprej potrditi. Poskusite z drugim virom.',
  tooLarge:
    'To sporočilo ima preveč prilog. Za nadaljevanje uredite prejšnje sporočilo ali odstranite datoteko.',
  unfinished:
    'Zahteva se ni dokončala. Uredite prejšnje sporočilo ali odstranite priloge in poskusite znova.',
  editingAppUnavailable:
    'Ta klepet ne more več spreminjati svoje aplikacije. Odprite aplikacijo v razdelku Aplikacije in izberite Spremeni v klepetu, da nadaljujete delo z njo.',
  openApps: 'Odpri Aplikacije',
  editingAppUnreachable:
    'Klepet trenutno ni mogel odpreti shranjene različice te aplikacije. Poskusite znova čez trenutek ali odprite aplikacijo v razdelku Aplikacije.',
  editingPreviewGone:
    'Predogled te aplikacije ni več na voljo. Nadaljujte brez predogleda ali odprite shranjeno aplikacijo v razdelku Aplikacije.',
  continueWithoutPreview: 'Nadaljuj brez predogleda',
  planRequired: 'Vaše podjetje nima aktivnega paketa.',
  creditsExhausted: 'Porabili ste vse kredite.',
  memberLimitReached: 'Dosegli ste mesečno omejitev.',
  paymentRequired: 'Vaš paket trenutno ne omogoča novih sporočil.',
};
const sv: Copy = {
  input:
    'Vi kunde inte svara på det här meddelandet. Om det har en bifogad fil, försök skicka det utan filen.',
  tool: 'Det här steget kördes inte eftersom kostnaden inte kunde bekräftas i förväg. Prova en annan källa.',
  tooLarge:
    'Det här meddelandet har för många bilagor. Redigera ett tidigare meddelande eller ta bort en fil för att fortsätta.',
  unfinished:
    'Förfrågan slutfördes inte. Redigera ett tidigare meddelande eller ta bort bilagor och försök igen.',
  editingAppUnavailable:
    'Den här chatten kan inte längre ändra sin app. Öppna appen under Appar och välj Ändra i chatten för att fortsätta arbeta med den.',
  openApps: 'Öppna Appar',
  editingAppUnreachable:
    'Chatten kunde inte öppna den sparade versionen av den här appen just nu. Försök igen om en stund eller öppna appen under Appar.',
  editingPreviewGone:
    'Förhandsvisningen av den här appen är inte längre tillgänglig. Fortsätt utan förhandsvisningen eller öppna en sparad app från Appar.',
  continueWithoutPreview: 'Fortsätt utan förhandsvisningen',
  planRequired: 'Företaget har ingen aktiv plan.',
  creditsExhausted: 'Du har använt alla krediter.',
  memberLimitReached: 'Du har nått din månadsgräns.',
  paymentRequired: 'Din plan tillåter inte nya meddelanden just nu.',
};

export const chatErrorCopy: NamespaceCatalog<ChatErrorKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default chatErrorCopy;
